import { fetchSiteLanguages, type SiteLanguages } from './ad-language-api';
import { adApiRequest } from './banner-kit-api';
import { namesOf, type ListNames } from './owner-game-lists-api';

/**
 * One of the brand's own lists named in every language of its website (`list-name-localize`, a free synchronous op of
 * spark-creatives): Lumio translates the name as a casino lobby's section heading, in the market's gambling terms, and
 * keeps every provider name in `keep` that the name holds exactly as written. English is always asked for: it is the
 * title the site falls back to in a language the list has no name in.
 */

/** The `/creatives/generate` media type (and constant prompt) of the list name op. */
const LOCALIZE_MEDIA_TYPE = 'list-name-localize';
const LOCALIZE_PROMPT = 'list name localize';

/** The most provider names sent along (the service's bound). */
const KEEP_MAX = 300;

/** How long the translation may take before the list is saved without it: a create or rename waits for it. */
const LOCALIZE_TIMEOUT_MS = 15000;

/** The longest a list's name is in one language, as spark-creatives takes it (its translations included). The name
 *  itself keeps the list editor's cap. */
export const LANGUAGE_NAME_MAX = 100;

/** The languages a list is named in: English, then the brand website's (`site`, as read by `fetchSiteLanguages` or
 *  `useSiteLanguages`). English alone when the website has no other, or when its languages could not be read: a list
 *  is then named as before, its one name in every language. */
export const nameLanguagesOf = (site: SiteLanguages | null | undefined): string[] => [
  ...new Set(['en', ...(site?.known ? site.languages : [])]),
];

/** `name` in each of the brand website's languages; null when the website has no language but English, when the
 *  translation failed or took longer than LOCALIZE_TIMEOUT_MS (the caller stores none). Only the providers the name
 *  holds are sent to keep. */
export const localizeListName = async (
  tenantId: number,
  name: string,
  keep: readonly string[],
): Promise<ListNames | null> => {
  // Read once per tenant (the heading's languages are the same read): asked again only after one that failed.
  const langs = nameLanguagesOf(await fetchSiteLanguages(tenantId));
  // English only: the name as typed is the title (asking would only let the translation reword it).
  if (langs.length < 2) return null;
  const lower = name.toLowerCase();
  const kept = [...new Set(keep)].filter((k) => k !== '' && lower.includes(k.toLowerCase())).slice(0, KEEP_MAX);
  const body = {
    tenant_id: String(tenantId),
    placement: 'banner_kit',
    prompt: LOCALIZE_PROMPT,
    media_type: LOCALIZE_MEDIA_TYPE,
    ad: { params: { name, languages: langs, keep: kept } },
  };
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), LOCALIZE_TIMEOUT_MS);
  const res = await adApiRequest<{ ok?: boolean; names?: unknown }>('post', '/creatives/generate', body, ctrl.signal);
  clearTimeout(timer);
  if (!res.ok || res.data.ok === false) return null;
  return namesOf(res.data.names);
};
